/**
 * Your books ([D-144](../../../docs/DECISIONS.md#d-144), mockup 04).
 *
 * The course as a shelf: every book with its progress, the one the child is in
 * open, the ones ahead of them waiting. Tapping an open book turns the path to
 * where they are in it. A locked book says what opens it rather than just
 * being grey — "Opens after Book 2" is a goal, a greyed card is a wall.
 *
 * Which books are open is the server's answer, from the same rule the path
 * uses, so the shelf and the path cannot disagree.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Client } from './api.js';
import Sneakers from './Sneakers.js';

type Shelf = Awaited<ReturnType<Client['path']['books']['query']>>;

/** A cover per book, in the colours the curriculum gives them. */
const COVERS = ['📗', '📘', '📙', '📕', '📓'];

export const cover = (number: number): string => COVERS[number - 1] ?? '📔';

export default function Books({
  client,
  onOpen,
  onBack,
}: {
  readonly client: Client;
  /** The chapter to turn the path to. */
  readonly onOpen: (chapterId: string) => void;
  readonly onBack: () => void;
}) {
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [trouble, setTrouble] = useState(false);

  const load = useCallback(() => {
    setTrouble(false);
    Promise.resolve()
      .then(() => client.path.books.query())
      .then(setShelf)
      .catch(() => {
        setTrouble(true);
      });
  }, [client]);

  useEffect(load, [load]);

  if (trouble) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          The books would not load. Please try again.
        </p>
        <button type="button" className="btn" style={{ marginTop: 12 }} onClick={load}>
          Try again
        </button>
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 10 }}
          onClick={onBack}
        >
          Back
        </button>
      </div>
    );
  }

  if (shelf === null) {
    return (
      <div className="grow pad center">
        <p className="sub">One moment…</p>
      </div>
    );
  }

  // The book they are in: the last one open, or the last finished if all are.
  const here =
    [...shelf].reverse().find((book) => book.state === 'open') ?? shelf.at(-1) ?? null;

  return (
    <div className="grow pad">
      <h2>Your books</h2>
      {here !== null && (
        <Sneakers
          text={`You are on Book ${String(here.number)}!`}
          audioRef={null}
          size={48}
        />
      )}

      <div style={{ marginTop: 12 }}>
        {shelf.map((book) => {
          const locked = book.state === 'locked';
          const share =
            book.chapterCount === 0
              ? 0
              : Math.round((100 * book.chaptersDone) / book.chapterCount);
          const before = shelf.find((one) => one.number === book.number - 1);
          return (
            <button
              key={book.bookId}
              type="button"
              className="card"
              disabled={locked}
              aria-label={
                locked
                  ? `Book ${String(book.number)}, ${book.titleL1}, opens after Book ${String(book.number - 1)}`
                  : `Book ${String(book.number)}, ${book.titleL1}, ${String(book.chaptersDone)} of ${String(book.chapterCount)} chapters`
              }
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                marginBottom: 11,
                opacity: locked ? 0.5 : 1,
                cursor: locked ? 'default' : 'pointer',
              }}
              onClick={() => {
                onOpen(book.openAt);
              }}
            >
              <div className="row" aria-hidden="true">
                <div style={{ fontSize: 31 }}>{cover(book.number)}</div>
                <div style={{ flex: 1 }}>
                  <h3>
                    {book.number} · {book.titleL1}
                  </h3>
                  <div className="tiny">
                    {locked && before !== undefined
                      ? `Opens after Book ${String(before.number)}`
                      : `${String(book.chaptersDone)} of ${String(book.chapterCount)} chapters`}
                  </div>
                  <div className="bar" style={{ marginTop: 7, height: 10 }}>
                    <i
                      style={{
                        width: `${String(share)}%`,
                        background:
                          book.state === 'done'
                            ? 'var(--gold)'
                            : locked
                              ? '#DCD3C4'
                              : 'var(--green)',
                      }}
                    />
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--muted)' }}>
                  {share}%
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 6 }}
        onClick={onBack}
      >
        Back
      </button>
    </div>
  );
}
